import Barcode from 'react-barcode';

import { forwardRef, ReactNode, useMemo } from 'react';
import { Accordion, ListGroup } from 'react-bootstrap';
import { startCase, kebabCase } from 'lodash';

import { ReactComponent as FullLogoSvg } from '@assets/images/full_logo.svg';
import { ReactComponent as NateraLogoSvg } from '@assets/images/natera/natera-web-logo.svg';
import {
  Requisition,
  RequisitionConsentGrant,
  RequisitionFamilyMember,
  RequisitionInsurance,
} from '@customTypes/requisition';
import { UserContact } from '@customTypes/clinician';
import { formatDateForDisplay } from '@helpers/date';
import { getRequisitionName } from '@helpers/requisition';
import useRequisition from '@hooks/useRequisition';
import { useSettings } from '@hooks/useSettings';
import { FVTSection } from './FVTSection';

import './RequisitionReceiptContent.scss';

interface IProps {
  requisition: Requisition;
}

interface RequisitionFieldToRender {
  label?: string;
  value: string | ReactNode[] | ReactNode;
  section?: string;
  unordered?: boolean;
  dataTestId?: string;
  fullWidth?: boolean;
}

const humanize = (value?: string): string => (value ? startCase(kebabCase(value)) : '');
const DISPLAY_VALUE_OVERRIDES: Record<string, string> = {
  YES: 'Yes',
  NO: 'No',
  TRUE: 'Yes',
  FALSE: 'No',
  true: 'Yes',
  false: 'No',
};
const formatConsentName = (name: string, isNatera: boolean): string =>
  isNatera
    ? name
        .replace(/\bMyOme\b/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    : name;
const formatConsentDates = (grant: RequisitionConsentGrant): ReactNode => (
  <>
    <span className='d-block'>{formatDateForDisplay(grant.granted_on) ?? ''}</span>
    {grant.valid_until && (
      <span className='d-block'>Valid Until: {formatDateForDisplay(grant.valid_until)}</span>
    )}
  </>
);
export const formatDisplayValue = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value !== 'string') return value == null ? '' : String(value);
  return DISPLAY_VALUE_OVERRIDES[value] ?? (/[_-]/.test(value) ? humanize(value) : value);
};
const formatName = (name?: { first: string; middle?: string | null; last: string }): string =>
  name ? [name.first, name.middle, name.last].filter(Boolean).join(' ') : '';
const formatObservationValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value
      .filter(item => hasDisplayableValue({ value: item }))
      .map(formatObservationValue)
      .join(', ');
  }
  if (value && typeof value === 'object') {
    return Object.entries(value)
      .filter(([, nestedValue]) => hasDisplayableValue({ value: nestedValue }))
      .map(([key, nestedValue]) => `${humanize(key)}: ${formatObservationValue(nestedValue)}`)
      .join('; ');
  }
  return formatDisplayValue(value);
};
export const formatObservation = (observationType: string, value: unknown): [string, string] => {
  if (observationType === 'secondary_findings_opt_out' && typeof value === 'boolean') {
    return ['Secondary Findings', value ? 'Opt-Out' : 'Opt-In'];
  }
  if (observationType === 'NDD_ICD10_CODES') {
    return ['Reason for Testing and ICD-10', formatObservationValue(value)];
  }
  if (observationType.startsWith('CLINICAL_PHENOTYPES__')) {
    return [
      `Clinical Phenotypes — ${humanize(observationType.replace('CLINICAL_PHENOTYPES__', ''))}`,
      formatObservationValue(value),
    ];
  }
  return [humanize(observationType), formatObservationValue(value)];
};

export const hasDisplayableValue = ({ value }: { value: unknown }): boolean => {
  if (value == null) return false;
  if (typeof value === 'boolean' || typeof value === 'number') return true;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.some(item => hasDisplayableValue({ value: item }));
  if (typeof value === 'object') return Object.values(value).some(item => hasDisplayableValue({ value: item }));
  return true;
};

// Drops blank fields, moving a dropped field's section header onto the next survivor so the
// section only disappears when every field in it is blank.
const dropBlankFields = (fields: RequisitionFieldToRender[]): RequisitionFieldToRender[] => {
  let carriedSection: string | undefined;

  return fields.flatMap(field => {
    if (!hasDisplayableValue(field)) {
      carriedSection = field.section ?? carriedSection;
      return [];
    }
    const section = field.section ?? carriedSection;
    carriedSection = undefined;
    return [{ ...field, section }];
  });
};

const spanUnpairedFields = (fields: RequisitionFieldToRender[]): RequisitionFieldToRender[] => {
  const result = fields.map(field => ({ ...field }));
  let run: number[] = [];

  const spanUnpairedField = () => {
    if (run.length % 2 === 1) result[run.at(-1)!].fullWidth = true;
    run = [];
  };

  result.forEach((field, index) => {
    if (field.fullWidth) spanUnpairedField();
    else run.push(index);
  });
  spanUnpairedField();

  return result;
};

const formatAddress = (contact?: UserContact): ReactNode => {
  const address = contact?.address;
  if (!address) return '';

  return (
    <>
      <span className='d-block'>{address.street_address}</span>
      {address.street_address_2 && <span className='d-block'>{address.street_address_2}</span>}
      <span className='d-block'>{[address.city, address.region, address.postal_code].filter(Boolean).join(', ')}</span>
      {address.country && <span className='d-block'>{address.country}</span>}
    </>
  );
};

const formatInsurance = (insurance: RequisitionInsurance): ReactNode => (
  <>
    <span className='d-block'>{insurance.company}</span>
    {insurance.phone && <span className='d-block'>Phone: {insurance.phone}</span>}
    <span className='d-block'>Member ID: {insurance.member_id}</span>
    {insurance.group_number && <span className='d-block'>Group Number: {insurance.group_number}</span>}
    {insurance.prior_auth_number && (
      <span className='d-block'>Prior Authorization Number: {insurance.prior_auth_number}</span>
    )}
    <span className='d-block'>
      Patient Relationship to Policyholder:{' '}
      {insurance.relationship_to_policyholder === 'OTHER'
        ? insurance.relationship_to_policyholder_other
        : humanize(insurance.relationship_to_policyholder)}
    </span>
  </>
);

const formatFamilyMember = (familyMember: RequisitionFamilyMember): ReactNode => {
  const contact = familyMember.subject?.contact_info?.[0];
  const fields: RequisitionFieldToRender[] = [
    ...(familyMember.requisition_id ? [{ label: 'ID', value: familyMember.requisition_id }] : []),
    { label: 'Relationship to Proband', value: humanize(familyMember.relationship_to_proband) },
    { label: 'Name', value: formatName(familyMember.subject?.name) },
    { label: 'Date of Birth', value: formatDateForDisplay(familyMember.subject?.demographics?.dob) ?? '' },
    { label: 'Sex', value: humanize(familyMember.subject?.demographics?.sex) },
    { label: 'Ancestry', value: familyMember.subject?.ancestry?.map(humanize).join(', ') ?? '' },
    { label: 'Address', value: formatAddress(contact), fullWidth: true },
    { label: 'Email', value: contact?.email ?? '' },
    { label: 'Phone', value: contact?.phone ?? '' },
    { label: 'MRN', value: familyMember.remote_mrn ?? '' },
    { label: 'Sample Type', value: humanize(familyMember.sample_request?.type) },
    {
      label: 'Send Kit to Patient',
      value:
        familyMember.sample_request?.send_kit === undefined
          ? ''
          : formatDisplayValue(familyMember.sample_request.send_kit),
    },
    {
      label: 'Kit Shipping Address',
      value: formatAddress(familyMember.sample_request?.send_kit_address),
      fullWidth: true,
    },
    {
      label: 'Secondary Findings',
      value:
        familyMember.secondary_findings_opt_out === undefined
          ? ''
          : familyMember.secondary_findings_opt_out
            ? 'Opt-Out'
            : 'Opt-In',
    },
    { label: 'Affliction Status', value: humanize(familyMember.affected_status) },
    { label: 'Clinical Phenotype', value: familyMember.clinical_phenotype ?? '', fullWidth: true },
    ...(familyMember.order_observations
      ?.filter(observation => !['secondary_findings_opt_out', 'affected_status'].includes(observation.observation_type))
      .map(observation => {
        const [label, value] = formatObservation(observation.observation_type, observation.value);
        return { label, value, fullWidth: typeof observation.value === 'object' };
      }) ?? []),
  ].filter(hasDisplayableValue);

  if (!fields.length) return null;

  return (
    <div className='receipt-family-fields'>
      {spanUnpairedFields(fields).map((field, index) => (
        <div
          className={`receipt-family-field ${field.fullWidth ? 'receipt-family-field-full' : ''}`}
          key={`${field.label}-${index}`}
        >
          <p className='mb-0 fs-6 fw-semibold field-label'>{field.label}</p>
          <span className='field-value'>{field.value}</span>
        </div>
      ))}
    </div>
  );
};

const LOGO_HEIGHT = 28;

export const RequisitionReceiptContent = forwardRef<HTMLDivElement, IProps>(({ requisition }, ref) => {
  const { clinicians, patientName, requisitionId, requisitionDate, orders } = useRequisition(requisition);
  const { isNatera } = useSettings();

  const fieldsToRender: RequisitionFieldToRender[] = useMemo(() => {
    if (!requisition) return [];

    const clinicName = requisition.clinic_name ?? requisition.clinicians?.[0]?.clinic_name;
    const res: RequisitionFieldToRender[] = [
      {
        label: 'Order ID',
        value: requisitionId,
        section: 'Provider',
        dataTestId: 'requisitionNumber',
      },
      {
        value: <Barcode value={requisitionId} width={1.5} height={50} displayValue={false} />,
        dataTestId: 'requisitionBarcode',
      },
      // Clinic and clinician are adjacent so the grid pairs them on one row.
      ...(clinicName ? [{ label: 'Clinic', value: clinicName }] : []),
      {
        label: clinicians.length > 1 ? 'Clinicians' : 'Clinician',
        value: clinicians.map(clinician => clinician.formattedName),
        dataTestId: 'clinicians',
      },
      {
        label: 'Order Date',
        value: requisitionDate,
        dataTestId: 'orderDate',
      },
      {
        label: 'Patient',
        value: requisition.subject?.name ? formatName(requisition.subject.name) : patientName,
        section: 'Patient Information',
        dataTestId: 'patient',
      },
    ];

    const contact = requisition.subject?.contact_info?.[0];
    const patientFields: RequisitionFieldToRender[] = [
      {
        label: 'Date of Birth',
        value: formatDateForDisplay(requisition.subject?.demographics?.dob) ?? '',
      },
      { label: 'Sex', value: humanize(requisition.subject?.demographics?.sex) },
      { label: 'Ancestry', value: requisition.ancestry?.map(humanize).join(', ') ?? '' },
      { label: 'Address', value: formatAddress(contact), fullWidth: true },
      { label: 'Email', value: contact?.email ?? '' },
      { label: 'Phone', value: contact?.phone ?? '' },
      { label: 'MRN', value: requisition.remote_mrn ?? requisition.external_mrn ?? '' },
    ];
    res.push(...patientFields);

    if (requisition.guardian) {
      res.push({
        label: 'Parent or Guardian',
        value: (
          <>
            <span className='d-block'>{formatName(requisition.guardian.name)}</span>
            <span className='d-block'>{formatDisplayValue(requisition.guardian.relationship_to_patient)}</span>
          </>
        ),
        fullWidth: true,
      });
    }

    res.push({
      label: orders?.length > 1 ? 'Tests' : 'Test',
      value: orders.map(order => order.product_name),
      section: 'Testing',
      unordered: true,
      dataTestId: 'tests',
      fullWidth: true,
    });
    if (requisition.indications) {
      res.push({ label: 'Clinical Indications', value: requisition.indications, fullWidth: true });
    }
    requisition.consent_grants?.forEach((grant, index) => {
      res.push({
        label: formatConsentName(grant.name, isNatera),
        value: formatConsentDates(grant),
        section: index === 0 ? 'Consent' : undefined,
      });
    });

    if (requisition.billing) {
      const billingType =
        requisition.billing.billing_type === 'SELF'
          ? 'Patient Pay'
          : formatDisplayValue(requisition.billing.billing_type);
      res.push({ label: 'Billing Type', value: billingType, section: 'Billing' });
      if (requisition.billing.billing_code) {
        res.push({ label: 'Billing Code', value: formatDisplayValue(requisition.billing.billing_code) });
      }
      const insuranceList = requisition.billing.insurance ?? [];
      insuranceList.forEach((insurance, index) => {
        res.push({
          label: insuranceList.length > 1 ? `Insurance ${index + 1}` : 'Insurance',
          value: formatInsurance(insurance),
          fullWidth: true,
        });
        if (insurance.epsdt_coverage) {
          const epsdt = insurance.epsdt_coverage;
          res.push({
            label: 'EPSDT Coverage',
            value: (
              <>
                {epsdt.medicaid_plan && <span className='d-block'>Medicaid Plan: {epsdt.medicaid_plan}</span>}
                {epsdt.medicaid_number && <span className='d-block'>Medicaid Number: {epsdt.medicaid_number}</span>}
                {epsdt.attestations?.map(attestation => (
                  <span className='d-block' key={attestation}>
                    {formatDisplayValue(attestation)}
                  </span>
                ))}
              </>
            ),
            fullWidth: true,
          });
        }
      });
    }

    if (requisition.sample_request) {
      res.push({
        label: 'Sample Type',
        value: humanize(requisition.sample_request.type),
        section: 'Specimen',
      });
      if (requisition.sample_request.send_kit !== undefined) {
        res.push({
          label: 'Send Kit to Patient',
          value: formatDisplayValue(requisition.sample_request.send_kit),
        });
      }
      if (requisition.sample_request.send_kit_address) {
        res.push({
          label: 'Kit Shipping Address',
          value: formatAddress(requisition.sample_request.send_kit_address),
          fullWidth: true,
        });
      }
    }

    const clinicalFields: RequisitionFieldToRender[] =
      requisition.order_observations?.map(observation => {
        const [label, value] = formatObservation(observation.observation_type, observation.value);
        return { label, value, fullWidth: typeof observation.value === 'object' };
      }) ?? [];
    if (clinicalFields.length) {
      clinicalFields[0].section = 'Clinical';
      res.push(...clinicalFields);
    }

    const familyMembers = requisition.family_members ?? [];
    const relationships = familyMembers.map(familyMember => humanize(familyMember.relationship_to_proband));
    familyMembers.forEach((familyMember, index) => {
      const relationship = relationships[index];
      const sameCount = relationships.filter(r => r === relationship).length;
      const ordinal = relationships.slice(0, index + 1).filter(r => r === relationship).length;
      // Unique relationship → "Parent"; duplicates → "Parent 1" among that group only.
      const section = relationship
        ? sameCount === 1
          ? relationship
          : `${relationship} ${ordinal}`
        : `Family ${index + 1}`;
      res.push({
        value: formatFamilyMember(familyMember),
        section,
        fullWidth: true,
      });
    });

    const familyRequisitionIds = new Set(
      familyMembers.flatMap(familyMember => (familyMember.requisition_id ? [familyMember.requisition_id] : [])),
    );
    const relatedRequisitions = requisition.case_requisitions?.filter(
      caseRequisition => !familyRequisitionIds.has(caseRequisition.requisition_id),
    );
    if (relatedRequisitions?.length) {
      res.push({
        label: relatedRequisitions.length > 1 ? 'Related Orders' : 'Related Order',
        section: 'Related Requisitions',
        value: relatedRequisitions.map(caseRequisition => (
          <>
            <p className='mb-0'>
              <span className='fw-medium'>ID: </span>
              {caseRequisition.requisition_id}
            </p>
            <p className='mb-0'>
              <span className='fw-medium'>Name: </span>
              {getRequisitionName(caseRequisition)}
            </p>
            <p className='mb-0'>
              <span className='fw-medium'>Relation: </span>
              {startCase(kebabCase(caseRequisition.relationship))}
            </p>
          </>
        )),
        unordered: true,
        fullWidth: true,
      });
    }
    if (requisition.fvt?.variants?.length) {
      res.push({
        label: requisition.orders[0]?.product_name || 'Test Name',
        section: 'Familial Variant Testing',
        value: <FVTSection fvt={requisition.fvt} />,
        unordered: true,
        dataTestId: 'fvtSection',
        fullWidth: true,
      });
    }

    return dropBlankFields(res);
  }, [requisition, clinicians, requisitionId, patientName, requisitionDate, orders, isNatera]);

  const sections = useMemo(
    () =>
      fieldsToRender
        .reduce<{ title: string; fields: RequisitionFieldToRender[] }[]>((result, field) => {
          if (field.section || result.length === 0) {
            result.push({ title: field.section ?? 'Provider', fields: [] });
          }
          result.at(-1)!.fields.push(field);
          return result;
        }, [])
        .map(section => ({ ...section, fields: spanUnpairedFields(section.fields) })),
    [fieldsToRender],
  );

  return (
    <div ref={ref} className='requisition-receipt-content-wrapper' data-openreplay-sanitize>
      <div className='receipt-brand'>
        {isNatera ? <NateraLogoSvg height={LOGO_HEIGHT} /> : <FullLogoSvg height={LOGO_HEIGHT} />}
        {requisitionId && (
          <p className='receipt-brand-id mb-0' data-testid='receiptRequisitionId'>
            {requisitionId}
          </p>
        )}
      </div>
      <Accordion
        alwaysOpen
        defaultActiveKey={['Provider', 'Patient Information', 'Testing']}
        className='mx-auto requisition-receipt-content'
      >
        {sections.map(section => (
          <Accordion.Item eventKey={section.title} key={section.title}>
            <Accordion.Header>{section.title}</Accordion.Header>
            <Accordion.Body className='p-0'>
              <ListGroup as='ul' variant='flush'>
                {section.fields.map((field, fieldIndex) => {
                  const isArray = Array.isArray(field.value);
                  const unordered = field.unordered && Array.isArray(field.value) && field.value.length > 1;

                  return (
                    <ListGroup.Item
                      as='li'
                      key={`${field.label ?? field.dataTestId}-${fieldIndex}`}
                      data-testid={field.dataTestId}
                      className={field.fullWidth ? 'receipt-field-full' : undefined}
                    >
                      {field.label && <p className='mb-0 fs-6 fw-semibold field-label'>{field.label}</p>}
                      {!isArray && <span className='field-value'>{field.value}</span>}
                      {isArray && unordered && (
                        <ul className='field-value unordered-receipt-item' data-testid='unorderedField'>
                          {(field.value as ReactNode[]).map((item, index) => (
                            <li key={index.toString()}>{item}</li>
                          ))}
                        </ul>
                      )}
                      {isArray && !unordered && (
                        <div className='field-value'>
                          {(field.value as ReactNode[]).map((item, index) => (
                            <span key={index.toString()} className='d-block'>
                              {item}
                            </span>
                          ))}
                        </div>
                      )}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
});

RequisitionReceiptContent.displayName = 'RequisitionReceiptContent';
